/* =====================================================================
   Cleonse custom product page — behaviour
   - Smooth-scroll CTAs to the theme's native product form
   - Accessible FAQ accordion (keyboard + aria-expanded)
   - Sticky mobile add-to-cart that drives the native product form
   Scoped to elements this page renders; no global theme behaviour is
   overridden.
   ===================================================================== */
(function () {
  'use strict';

  /* ---------- helpers ---------- */
  function findProductForm() {
    // Works across OS 2.0 themes (Dawn and most others) without hardcoded IDs.
    return (
      document.querySelector('form[action$="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('product-form form') ||
      null
    );
  }

  function scrollToProduct() {
    var form = findProductForm();
    var target =
      (form && (form.closest('.product, .product__info-wrapper, [id^="MainProduct"]') || form)) ||
      document.getElementById('MainProduct') ||
      document.querySelector('.shopify-section .product');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // nudge focus to the first variant/qty control for accessibility
      var focusable = form && form.querySelector('select, input:not([type=hidden]), button');
      if (focusable) { setTimeout(function () { try { focusable.focus({ preventScroll: true }); } catch (e) {} }, 400); }
      return true;
    }
    return false;
  }

  /* ---------- 1. smooth-scroll CTAs ---------- */
  // If a CTA's href is an in-page anchor (e.g. #ritual) that exists on this
  // page, scroll to that element (CSS scroll-margin-top handles the sticky
  // header offset). This lets the homepage point buttons at #ritual while the
  // product page — where there's no such anchor — falls back to the buy box.
  function anchorTargetFor(el) {
    var href = el.getAttribute('href') || '';
    var hash = href.charAt(0) === '#' ? href.slice(1) : '';
    if (!hash || hash === '#') return null;
    try { return document.getElementById(decodeURIComponent(hash)); }
    catch (e) { return document.getElementById(hash); }
  }

  function initScrollCtas() {
    document.querySelectorAll('[data-cl-scroll]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        // 1) explicit in-page anchor target (e.g. #ritual) wins
        var anchor = anchorTargetFor(el);
        if (anchor) {
          e.preventDefault();
          anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        // 2) otherwise scroll to the native product form if one exists
        if (findProductForm()) {
          e.preventDefault();
          scrollToProduct();
        }
        // 3) otherwise let the anchor href fall through as a fallback
      });
    });
  }

  /* ---------- 2. FAQ accordion ---------- */
  function initFaq() {
    document.querySelectorAll('.cl-faq-q').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        btn.setAttribute('aria-expanded', String(!expanded));
        if (panel) {
          if (expanded) {
            panel.style.maxHeight = null;
          } else {
            panel.style.maxHeight = panel.scrollHeight + 'px';
          }
        }
      });
    });
    // recompute open panel heights on resize
    window.addEventListener('resize', function () {
      document.querySelectorAll('.cl-faq-q[aria-expanded="true"]').forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
      });
    });
  }

  /* ---------- 3. sticky mobile ATC ---------- */
  function initStickyAtc() {
    var bar = document.querySelector('.cl-sticky-atc');
    if (!bar) return;
    var btn = bar.querySelector('[data-cl-atc]');
    if (!btn) return;

    btn.addEventListener('click', function (e) {
      var form = findProductForm();
      if (!form) return; // leave href fallback
      e.preventDefault();
      var submitBtn = form.querySelector('[type="submit"], [name="add"]');
      // Prefer clicking the theme's own ATC button so AJAX cart / drawer fires.
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.click();
      } else if (submitBtn && submitBtn.disabled) {
        // sold out — just scroll so the user sees state
        scrollToProduct();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });

    // Hide the sticky bar while the native product form is on screen so it
    // never covers the real buy box.
    var product = (findProductForm() || {}).closest
      ? findProductForm().closest('.product, [id^="MainProduct"], .shopify-section')
      : null;
    if (product && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.dataset.hidden = entry.isIntersecting ? 'true' : 'false';
          bar.style.transform = entry.isIntersecting ? 'translateY(120%)' : 'translateY(0)';
        });
      }, { threshold: 0 });
      bar.style.transition = 'transform .25s ease';
      io.observe(product);
    }
  }

  /* ---------- 4. mobile sliders (arrows + numbers) ---------- */
  function initSliders() {
    document.querySelectorAll('[data-cl-slider]').forEach(function (wrap) {
      var rail = wrap.querySelector('.cl-slider');
      if (!rail) return;
      var items = Array.prototype.slice.call(rail.children);
      if (!items.length) return;
      var prev = wrap.querySelector('[data-cl-prev]');
      var next = wrap.querySelector('[data-cl-next]');
      var nums = Array.prototype.slice.call(wrap.querySelectorAll('[data-cl-go]'));

      function current() {
        // index of the item whose centre is closest to the rail centre
        var railCentre = rail.scrollLeft + rail.clientWidth / 2;
        var best = 0, bestDist = Infinity;
        items.forEach(function (el, i) {
          var c = el.offsetLeft + el.offsetWidth / 2;
          var d = Math.abs(c - railCentre);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }
      function goTo(i) {
        i = Math.max(0, Math.min(items.length - 1, i));
        var el = items[i];
        rail.scrollTo({ left: el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2, behavior: 'smooth' });
      }
      function sync() {
        var i = current();
        nums.forEach(function (b, bi) { b.setAttribute('aria-current', bi === i ? 'true' : 'false'); });
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === items.length - 1;
      }

      if (prev) prev.addEventListener('click', function () { goTo(current() - 1); });
      if (next) next.addEventListener('click', function () { goTo(current() + 1); });
      nums.forEach(function (b) {
        b.addEventListener('click', function () { goTo(parseInt(b.getAttribute('data-cl-go'), 10)); });
      });

      var raf;
      rail.addEventListener('scroll', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      }, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function init() {
    initScrollCtas();
    initFaq();
    initStickyAtc();
    initSliders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
